import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField, Box, ToggleButton, ToggleButtonGroup, Typography, FormHelperText, MenuItem, Autocomplete, Alert,
} from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { setActiveStep, setView, updateFormData, updateFiles, type OnboardingFiles } from './onboardingSlice';
import FileUpload from '../../components/FileUpload';
import StepNav from './StepNav';
import { fetchCountries, fetchCurrencies, fetchTimezones } from '../../api/meta';
import {
  dialCodeOptions, parseDialCode, normalizeDialCode, isValidCompanyRegistration, normalizeCompanyRegistrationInput,
} from './kybConstants';

interface Country { id: string; name: string; timezones: string[]; defaultCurrency?: string; }
interface CurrencyOption { code: string; name: string; }
interface Timezone { name: string; utcOffsetStr: string; }

const CompanyDetails = () => {
  const dispatch: AppDispatch = useDispatch();
  const { formData, activeStep, files } = useSelector((state: RootState) => state.onboarding);
  const parsed = parseDialCode(formData.phone);

  const [companyType, setCompanyType] = useState(formData.companyType);
  const [companyName, setCompanyName] = useState(formData.companyName);
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState(formData.companyRegistrationNumber);
  const [vatNumber, setVatNumber] = useState(formData.vatNumber);
  const [phoneCountryCode, setPhoneCountryCode] = useState(normalizeDialCode(formData.phoneCountryCode || parsed.dialCode || '+44') || '+44');
  const [phoneNumber, setPhoneNumber] = useState(formData.ownerPhoneLocal || parsed.localNumber);
  const [street, setStreet] = useState(formData.street);
  const [streetLine2, setStreetLine2] = useState(formData.streetLine2);
  const [city, setCity] = useState(formData.city);
  const [province, setProvince] = useState(formData.province);
  const [zip, setZip] = useState(formData.zip);
  const [country, setCountry] = useState<Country | null>(null);
  const [currency, setCurrency] = useState<CurrencyOption | null>(null);
  const [timezone, setTimezone] = useState<Timezone | null>(null);
  const [currencyUserSet, setCurrencyUserSet] = useState(Boolean(formData.currency));
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [bankName, setBankName] = useState(formData.bankName);
  const [accountNumber, setAccountNumber] = useState(formData.accountNumber);
  const [iban, setIban] = useState(formData.iban);
  const [swift, setSwift] = useState(formData.swift);
  const [sortCode, setSortCode] = useState(formData.sortCode);
  const [touched, setTouched] = useState(false);
  const [metaError, setMetaError] = useState('');

  const isCompany = companyType === 'COMPANY';
  const commonAccept = 'application/pdf,image/*';
  const maxBytes = 10 * 1024 * 1024;

  useEffect(() => {
    (async () => {
      try {
        const [c, tz, cur] = await Promise.all([fetchCountries(), fetchTimezones(), fetchCurrencies()]);
        setCountries(c || []);
        setTimezones(tz);
        setCurrencies(cur || []);
        if (formData.country) setCountry((c || []).find((co) => co.id === formData.country) || null);
        if (formData.currency) {
          setCurrency((cur || []).find((x) => x.code === formData.currency) || { code: formData.currency, name: formData.currency });
        }
        if (formData.timezone) setTimezone(tz.find((t) => t.name === formData.timezone) || null);
        setMetaError('');
      } catch (_) {
        setMetaError('Could not load countries, currencies, or timezones. Check your connection and refresh.');
      }
    })();
  }, [formData.country, formData.currency, formData.timezone]);

  const applyCurrencyForCountry = (newCountry: Country | null, force = false) => {
    if (!newCountry?.defaultCurrency) return;
    if (!force && currencyUserSet && currency) return;
    const match = currencies.find((x) => x.code === newCountry.defaultCurrency);
    setCurrency(match || { code: newCountry.defaultCurrency!, name: newCountry.defaultCurrency! });
  };

  const handleCountryChange = (_e: React.SyntheticEvent, newValue: Country | null) => {
    setCountry(newValue);
    setCurrencyUserSet(false);
    applyCurrencyForCountry(newValue, true);
    if (newValue?.timezones?.length) {
      const primaryTimezone = timezones.find((t) => t.name === newValue.timezones[0]);
      setTimezone(primaryTimezone || null);
    } else {
      setTimezone(null);
    }
  };

  const validCompanyDocs = Boolean(files.companyRegistration && files.proofOfAddress);
  const validBankDocs = Boolean(files.proofOfAccount);
  const validBank = Boolean(bankName.trim() && accountNumber.trim() && validBankDocs);
  const validAddress = Boolean(street.trim() && city.trim() && zip.trim() && country);
  const validPhone = Boolean(phoneCountryCode && phoneNumber.trim());
  const regCountry = country?.id || formData.country || '';
  const validRegNumber = !isCompany || isValidCompanyRegistration(companyRegistrationNumber, regCountry);
  const validCompany = isCompany && validRegNumber && validCompanyDocs;
  const validNonCompany = !isCompany && validCompanyDocs;
  const valid = Boolean(validPhone && companyName.trim() && validAddress && currency && timezone && validBank && (validCompany || validNonCompany));

  const missing: string[] = [];
  if (!companyName.trim()) missing.push('Official business name');
  if (isCompany && !companyRegistrationNumber.trim()) missing.push('Company registration number');
  else if (isCompany && !validRegNumber) missing.push('Company registration number is not in a valid format');
  if (!validPhone) missing.push('Phone number');
  if (!street.trim()) missing.push('Address line 1');
  if (!city.trim()) missing.push('City');
  if (!zip.trim()) missing.push('Postal code');
  if (!country) missing.push('Registration country (pick from the list)');
  if (!currency) missing.push('Currency (pick from the list)');
  if (!timezone) missing.push('Timezone (pick from the list)');
  if (!files.companyRegistration) missing.push('Certificate of incorporation / registration');
  if (!files.proofOfAddress) missing.push('Proof of company address');
  if (!bankName.trim()) missing.push('Bank name');
  if (!accountNumber.trim()) missing.push('Account number');
  if (!files.proofOfAccount) missing.push('Proof of bank account');

  const regHelperText = (() => {
    if (!isCompany) return '';
    const isGb = ['GB', 'UK'].includes(String(regCountry || '').toUpperCase().slice(0, 2));
    if (touched && !companyRegistrationNumber.trim()) return 'Company registration number is required';
    if (touched && !validRegNumber) {
      return isGb
        ? 'UK format: 8 digits (e.g. 12345678) or 2 letters + 6 digits (e.g. OC123456)'
        : 'Enter the official company registration / Companies House number (not the company name)';
    }
    return isGb
      ? 'UK Companies House number: 8 digits or 2 letters + 6 digits'
      : 'Official registration number from your company registry';
  })();

  const handleNext = () => {
    setTouched(true);
    if (!valid) return;
    const fullPhone = `${phoneCountryCode}${phoneNumber.replace(/\D/g, '')}`;
    dispatch(updateFormData({
      companyType,
      companyName: companyName.trim(),
      companyRegistrationNumber: companyRegistrationNumber.trim(),
      vatNumber: vatNumber.trim(),
      phoneCountryCode,
      ownerPhoneLocal: phoneNumber.replace(/\D/g, ''),
      phone: fullPhone,
      street,
      streetLine2,
      city,
      province,
      zip,
      country: country?.id || '',
      currency: currency?.code || '',
      timezone: timezone?.name || '',
      bankName: bankName.trim(),
      accountNumber: accountNumber.trim(),
      iban: iban.trim(),
      swift: swift.trim(),
      sortCode: sortCode.trim(),
    }));
    dispatch(setActiveStep(activeStep + 1));
  };

  const handleFile = (key: keyof OnboardingFiles, file: File) => {
    dispatch(updateFiles({ [key]: file } as Partial<OnboardingFiles>));
  };

  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Legal structure</Typography>
      <ToggleButtonGroup color="primary" exclusive value={companyType} onChange={(_, val) => val && setCompanyType(val)} sx={{ mb: 2 }}>
        <ToggleButton value="COMPANY">Company</ToggleButton>
        <ToggleButton value="INDIVIDUAL">Individual</ToggleButton>
        <ToggleButton value="SOLE_TRADER">Sole Trader</ToggleButton>
      </ToggleButtonGroup>

      <TextField label="Official Business Name" fullWidth margin="normal" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required error={touched && !companyName.trim()} helperText={touched && !companyName.trim() ? 'Official business name is required' : ''} />
      {isCompany && (
        <TextField
          label="Company Registration Number"
          fullWidth
          margin="normal"
          value={companyRegistrationNumber}
          onChange={(e) => setCompanyRegistrationNumber(normalizeCompanyRegistrationInput(e.target.value, regCountry))}
          required
          error={touched && !validRegNumber}
          helperText={regHelperText}
        />
      )}
      <TextField
        label="VAT Number"
        fullWidth
        margin="normal"
        value={vatNumber}
        onChange={(e) => setVatNumber(e.target.value)}
        helperText="Optional. If blank, we submit 0 to the payment partner."
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2, mt: 1 }}>
        <TextField select label="Phone Country Code" value={phoneCountryCode} onChange={(e) => setPhoneCountryCode(e.target.value)} required error={touched && !phoneCountryCode}>
          {dialCodeOptions(phoneCountryCode).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))} required error={touched && !phoneNumber.trim()} />
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Registered company address</Typography>
      <TextField label="Address Line 1" fullWidth margin="normal" value={street} onChange={(e) => setStreet(e.target.value)} required error={touched && !street.trim()} />
      <TextField label="Address Line 2" fullWidth margin="normal" value={streetLine2} onChange={(e) => setStreetLine2(e.target.value)} />
      <TextField label="City" fullWidth margin="normal" value={city} onChange={(e) => setCity(e.target.value)} required error={touched && !city.trim()} />
      <TextField label="State / Province" fullWidth margin="normal" value={province} onChange={(e) => setProvince(e.target.value)} />
      <TextField label="Postal Code" fullWidth margin="normal" value={zip} onChange={(e) => setZip(e.target.value)} required error={touched && !zip.trim()} />
      {metaError && <Alert severity="error" sx={{ mt: 1 }}>{metaError}</Alert>}
      <Autocomplete
        options={countries}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={country}
        onChange={handleCountryChange}
        renderInput={(params) => <TextField {...params} label="Registration Country" margin="normal" required error={touched && !country} helperText={touched && !country ? 'Select a country from the list' : ''} />}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px' }}>
          <Autocomplete options={currencies} getOptionLabel={(o) => `${o.code} — ${o.name}`} value={currency} isOptionEqualToValue={(a, b) => a.code === b.code} onChange={(_e, v) => { setCurrencyUserSet(true); setCurrency(v); }} renderInput={(params) => <TextField {...params} label="Currency" margin="normal" required error={touched && !currency} helperText={touched && !currency ? 'Select a currency from the list' : ''} />} />
        </Box>
        <Box sx={{ flex: '1 1 240px' }}>
          <Autocomplete options={timezones} getOptionLabel={(o) => `(GMT${o.utcOffsetStr}) ${o.name}`} isOptionEqualToValue={(a, b) => a.name === b.name} value={timezone} onChange={(_e, v) => setTimezone(v)} renderInput={(params) => <TextField {...params} label="Timezone" margin="normal" required error={touched && !timezone} helperText={touched && !timezone ? 'Select a timezone from the list' : ''} />} />
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Company Supporting Documents</Typography>
        <FileUpload label="Certificate of Incorporation / Registration" onFileSelect={(f) => handleFile('companyRegistration', f)} selectedFile={files.companyRegistration} accept={commonAccept} maxBytes={maxBytes} required error={touched && !files.companyRegistration ? 'This document is required' : null} />
        <FileUpload label="Proof of Company Address" onFileSelect={(f) => handleFile('proofOfAddress', f)} selectedFile={files.proofOfAddress} accept={commonAccept} maxBytes={maxBytes} helperText="Utility bill or bank statement" required error={touched && !files.proofOfAddress ? 'This document is required' : null} />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Settlement bank account</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Enter the account where settlement payouts should be paid, and upload a matching bank statement.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Bank name"
            fullWidth
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            required
            error={touched && !bankName.trim()}
            helperText={touched && !bankName.trim() ? 'Bank name is required' : ''}
          />
          <TextField
            label="Account number"
            fullWidth
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            required
            error={touched && !accountNumber.trim()}
            helperText={touched && !accountNumber.trim() ? 'Account number is required' : ''}
          />
          <TextField label="IBAN" fullWidth value={iban} onChange={(e) => setIban(e.target.value)} helperText="Required for most EU accounts" />
          <TextField label="BIC / Swift" fullWidth value={swift} onChange={(e) => setSwift(e.target.value)} />
          <TextField label="Sort code" fullWidth value={sortCode} onChange={(e) => setSortCode(e.target.value)} helperText="UK accounts" />
        </Box>
        <Box sx={{ mt: 1 }}>
          <FileUpload
            label="Proof of Bank Account"
            onFileSelect={(f) => handleFile('proofOfAccount', f)}
            selectedFile={files.proofOfAccount}
            accept={commonAccept}
            maxBytes={maxBytes}
            helperText="Bank statement showing account name and number / IBAN. PDF or image, max 10MB"
            required
            error={touched && !files.proofOfAccount ? 'This document is required' : null}
          />
        </Box>
        {touched && !validBank && <FormHelperText error>Bank name, account number, and proof of bank account are required</FormHelperText>}
      </Box>

      <StepNav
        onBack={() => dispatch(setView('hub'))}
        onNext={handleNext}
        missing={missing}
        showMissing={touched}
      />
    </>
  );
};

export default CompanyDetails;
